/**
 * AppRunnerScreen - Renders a user-created app in a sandboxed iframe
 *
 * Fetches the app's launch config (including preview_html) from app-registry
 * via the gateway proxy, then renders it in a sandboxed iframe.
 *
 * Route: /apps/:appId
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getLaunchConfig, type LaunchConfig } from '../services/appRegistry';

const AppRunnerScreen: React.FC = () => {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const [launchConfig, setLaunchConfig] = useState<LaunchConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!appId) {
      setError('No app ID provided');
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchLaunchConfig() {
      try {
        const config = await getLaunchConfig(appId!);
        if (!cancelled) {
          setLaunchConfig(config);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[AppRunner] Failed to load app:', err);
          setError(err instanceof Error ? err.message : 'Failed to load app');
          setLoading(false);
        }
      }
    }

    fetchLaunchConfig();
    return () => { cancelled = true; };
  }, [appId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mx-auto mb-4" />
          <p className="text-cyan-400/70 text-sm">Loading app...</p>
        </div>
      </div>
    );
  }

  if (error || !launchConfig) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-950">
        <div className="text-center max-w-md">
          <p className="text-red-400 text-lg mb-2">Failed to load app</p>
          <p className="text-gray-400 text-sm mb-6">{error || 'App not found'}</p>
          <button
            onClick={() => navigate('/apps')}
            className="px-6 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg border border-cyan-500/30 hover:bg-cyan-500/30 transition-colors"
          >
            Back to My Apps
          </button>
        </div>
      </div>
    );
  }

  const hasPreviewHtml = launchConfig.preview_html && launchConfig.preview_html.trim().length > 0;

  return (
    <div className="flex flex-col h-screen bg-gray-950">
      {/* App Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-cyan-500/20 bg-gray-950/95 backdrop-blur-sm">
        <button
          onClick={() => navigate('/apps')}
          className="p-2 rounded-lg hover:bg-cyan-500/10 transition-colors"
          aria-label="Back to apps"
        >
          <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-cyan-400 font-semibold text-lg truncate">{launchConfig.name}</h1>
          {launchConfig.description && (
            <p className="text-gray-400 text-xs truncate">{launchConfig.description}</p>
          )}
        </div>
        <button
          onClick={() => navigate('/apps/create', { state: { editApp: { app_id: appId, name: launchConfig.name } } })}
          className="px-3 py-1.5 text-xs text-cyan-400/70 border border-cyan-500/20 rounded-lg hover:bg-cyan-500/10 transition-colors"
        >
          Edit
        </button>
      </div>

      {/* App Content */}
      <div className="flex-1 relative">
        {hasPreviewHtml ? (
          <iframe
            srcDoc={launchConfig.preview_html}
            className="w-full h-full border-0"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            title={launchConfig.name}
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-md">
              <div className="w-16 h-16 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-cyan-400/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </div>
              <p className="text-cyan-400/70 text-sm mb-2">This app hasn't been built yet</p>
              <p className="text-gray-500 text-xs mb-6">Open the editor to build this app with AI</p>
              <button
                onClick={() => navigate('/apps/create', { state: { editApp: { app_id: appId, name: launchConfig.name } } })}
                className="px-6 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg border border-cyan-500/30 hover:bg-cyan-500/30 transition-colors"
              >
                Open Editor
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppRunnerScreen;
